import { canonicalize } from './canonical.js';
import { digestHex, signHex, verifyHex, ZERO_HASH, isHex } from './crypto.js';
import { Ledger, validateGenesis } from './ledger.js';
import type { Checkpoint, CheckpointBody, EntryBody, GenesisConfig, LogEntry, SignedRequest, SystemEvent } from './types.js';

/**
 * Hash-chained, sequencer-signed log. Each entry commits to the previous one,
 * so changing, inserting, removing or reordering anything changes every hash
 * after it. Each entry is signed by the sequencer, so a user holding a receipt
 * can prove what the operator promised at that position.
 */

export function bodyOf(e: LogEntry): EntryBody {
  return { seq: e.seq, prev: e.prev, ts: e.ts, kind: e.kind, data: e.data, result: e.result };
}

export function entryHash(body: EntryBody): string {
  return digestHex('entry', body);
}

export function sealEntry(body: EntryBody, sequencerSecret: string): LogEntry {
  const hash = entryHash(body);
  return { ...body, hash, sig: signHex(hash, sequencerSecret) };
}

export function checkpointHash(body: CheckpointBody): string {
  return digestHex('checkpoint', body);
}

export function sealCheckpoint(body: CheckpointBody, sequencerSecret: string): Checkpoint {
  return { ...body, sig: signHex(checkpointHash(body), sequencerSecret) };
}

export function verifyCheckpointSig(cp: Checkpoint, sequencerPub: string): boolean {
  const body: CheckpointBody = { seq: cp.seq, head: cp.head, stateRoot: cp.stateRoot, ts: cp.ts };
  return verifyHex(cp.sig, checkpointHash(body), sequencerPub);
}

/** Memo published on Zcash for each checkpoint (well under the 512-byte memo limit). */
export function anchorMemo(cp: CheckpointBody): string {
  return `DAZZLE1 cp ${cp.seq} ${cp.head} ${cp.stateRoot}`;
}

export function parseAnchorMemo(memo: string): { seq: number; head: string; stateRoot: string } | null {
  const m = /^DAZZLE1 cp (\d+) ([0-9a-f]{64}) ([0-9a-f]{64})$/.exec(memo.trim());
  if (!m) return null;
  return { seq: Number(m[1]), head: m[2], stateRoot: m[3] };
}

export class ChainError extends Error {
  constructor(
    public seq: number,
    message: string,
  ) {
    super(`entry ${seq}: ${message}`);
  }
}

/**
 * Replays a log from genesis, checking everything it can check offline:
 * order, hash links, hashes, sequencer signatures, user signatures (inside
 * the ledger) and that each stored result is exactly what the rules produce.
 */
export class Chain {
  ledger: Ledger | null = null;
  genesis: GenesisConfig | null = null;
  sequencer: string | null = null;
  head = ZERO_HASH;
  seq = -1;
  entries: LogEntry[] = [];

  constructor(private opts: { keepEntries?: boolean; expectSequencer?: string } = {}) {}

  apply(e: LogEntry): void {
    const s = e?.seq;
    if (s !== this.seq + 1) throw new ChainError(s, `out of order, expected ${this.seq + 1}`);
    if (e.prev !== this.head) throw new ChainError(s, 'prev hash does not link to previous entry');
    if (!isHex(e.hash, 32)) throw new ChainError(s, 'bad hash field');
    let h: string;
    try {
      h = entryHash(bodyOf(e));
    } catch (err) {
      throw new ChainError(s, `not canonical: ${(err as Error).message}`);
    }
    if (h !== e.hash) throw new ChainError(s, 'hash does not match content (entry was edited)');

    if (s === 0) {
      if (e.kind !== 'genesis') throw new ChainError(s, 'first entry must be genesis');
      const g = e.data as GenesisConfig;
      try {
        validateGenesis(g);
      } catch (err) {
        throw new ChainError(s, (err as Error).message);
      }
      if (this.opts.expectSequencer && g.sequencer !== this.opts.expectSequencer)
        throw new ChainError(s, 'genesis sequencer key is not the expected one');
      if (!verifyHex(e.sig, e.hash, g.sequencer)) throw new ChainError(s, 'bad sequencer signature');
      if (canonicalize(e.result) !== canonicalize({ ok: true })) throw new ChainError(s, 'bad genesis result');
      this.genesis = g;
      this.sequencer = g.sequencer;
      this.ledger = new Ledger(g);
    } else {
      if (!this.ledger || !this.sequencer) throw new ChainError(s, 'no genesis');
      if (!verifyHex(e.sig, e.hash, this.sequencer)) throw new ChainError(s, 'bad sequencer signature');
      let result: unknown;
      try {
        if (e.kind === 'user') result = this.ledger.execUser(e.data as SignedRequest, s, e.ts, (e.result as { sigs?: unknown } | null)?.sigs);
        else if (e.kind === 'system') result = this.ledger.execSystem(e.data as SystemEvent, s, e.ts);
        else throw new Error(`unexpected kind ${e.kind}`);
      } catch (err) {
        throw new ChainError(s, `rejected by rules: ${(err as Error).message}`);
      }
      if (canonicalize(result) !== canonicalize(e.result))
        throw new ChainError(s, 'stored result differs from what the rules produce');
    }
    this.head = e.hash;
    this.seq = s;
    if (this.opts.keepEntries) this.entries.push(e);
  }
}

export function genesisEntry(g: GenesisConfig, sequencerSecret: string, ts: number): LogEntry {
  validateGenesis(g);
  return sealEntry({ seq: 0, prev: ZERO_HASH, ts, kind: 'genesis', data: g, result: { ok: true } }, sequencerSecret);
}
